/**
 * conference-hero block (1 use)
 *
 * Conference landing page hero with hero image, title, description,
 * location/date badges, and a divider leading to event details.
 * Contains mostly hardcoded content for RSA/Identiverse/Oktane conferences.
 *
 * Hugo template: blocks/conference-hero.html
 */
interface EventDetail {
  heading?: string;
  copy?: string;
  copy_mobile?: string;
  image?: string;
  tags?: string[];
  date_tags?: string[];
  cta?: { text: string; url: string };
}

export function ConferenceHero({
  hero_image,
  hero_image_xl,
  title,
  description,
  description_2,
  location_badges,
  events,
}: {
  hero_image?: string;
  hero_image_xl?: string;
  title?: string;
  description?: string;
  description_2?: string;
  location_badges?: string[];
  events?: EventDetail[];
}) {
  return (
    <div className="px-4 md:px-8 pb-16 lg:pb-28">
      <div className="max-w-screen-xl mx-auto">
        {/* Hero image */}
        {(hero_image || hero_image_xl) && (
          <div className="rounded-2xl overflow-hidden">
            {hero_image_xl && (
              <img
                src={hero_image_xl}
                alt=""
                className="hidden xl:block w-full"
                loading="lazy"
              />
            )}
            {hero_image && (
              <img
                src={hero_image}
                alt=""
                className={hero_image_xl ? "xl:hidden w-full" : "w-full"}
                loading="lazy"
              />
            )}
          </div>
        )}

        {/* Title and description */}
        {title && (
          <h1 className="font-display text-charcoal font-bold text-25 mt-12 md:mt-16 lg:text-31 max-w-3xl">
            {title}
          </h1>
        )}
        {description && (
          <p className="font-body text-gray-80 text-25 mt-4 md:mt-5 max-w-5xl">
            {description}
          </p>
        )}
        {description_2 && (
          <p className="font-body text-gray-80 text-25 mt-4 max-w-5xl">
            {description_2}
          </p>
        )}

        {/* Location badges */}
        {location_badges && location_badges.length > 0 && (
          <ul className="flex flex-wrap gap-4 mt-6 max-w-xl">
            {location_badges.map((badge, i) => (
              <li
                key={i}
                className="font-medium text-base text-teal-dark bg-teal-dark/10 rounded-full px-3.5 py-1.5 leading-tight flex items-center gap-1.5"
              >
                {badge}
              </li>
            ))}
          </ul>
        )}

        <hr className="border-gray-60 mt-12 md:mt-16 lg:mt-20 2xl:mt-28" />

        {/* Event details */}
        {events?.map((event, i) => (
          <div
            key={i}
            className="grid gap-6 lg:gap-10 lg:grid-cols-12 mt-9 lg:mt-10 2xl:mt-16 group"
          >
            {event.image && (
              <div className="lg:col-span-5">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={event.image}
                    alt=""
                    className="group-hover:scale-[1.05] transition-all duration-500 w-full"
                    loading="lazy"
                  />
                </div>
              </div>
            )}
            <div className="lg:col-span-7">
              <div>
                {event.heading && (
                  <h2 className="font-display text-charcoal font-bold text-25">
                    {event.heading}
                  </h2>
                )}
                {event.tags && (
                  <ul className="flex flex-wrap gap-1.5 mt-2">
                    {event.tags.map((tag, j) => (
                      <li
                        key={j}
                        className="uppercase font-medium text-xxs text-gray-80 bg-[#EBEBE9] rounded px-2 py-1 leading-tight tracking-widest-4"
                      >
                        {tag}
                      </li>
                    ))}
                  </ul>
                )}
                {event.copy && (
                  <p className="font-body text-gray-80 mt-4">{event.copy}</p>
                )}
                {event.date_tags && (
                  <ul className="flex flex-wrap gap-1.5 mt-5 max-w-xl">
                    {event.date_tags.map((tag, j) => (
                      <li
                        key={j}
                        className="font-medium text-13 text-teal-dark bg-teal-dark/10 rounded-full px-2.5 py-0.5 leading-tight"
                      >
                        {tag}
                      </li>
                    ))}
                  </ul>
                )}
                {event.cta && (
                  <a
                    href={event.cta.url}
                    target="_blank"
                    rel="noopener"
                    className="mt-7 inline-flex items-center px-8 py-3 font-body font-medium leading-none text-center rounded-md text-white bg-teal-cta hover:bg-teal-cta-hover active:bg-teal-cta-hover/90"
                  >
                    <span>{event.cta.text}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
